import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { Link } from "react-router-dom";

const norm = (s) => String(s || "").toLowerCase().trim();

const displayStock = (v) => (typeof v === "number" ? (v < 0 ? "Non géré" : v) : "—");

function summarizeVariants(variants = []) {
  const vars = Array.isArray(variants) ? variants : [];

  const sizes = [];
  let totalManaged = 0;
  let lowCount = 0;
  let hasAny = 0;

  for (const v of vars) {
    if (!v) continue;
    const size = v.size != null ? String(v.size) : null;
    const stock = typeof v.stock === "number" ? v.stock : null;

    if (size) sizes.push(size);
    hasAny++;

    if (typeof stock === "number") {
      if (stock >= 0) totalManaged += stock;
      if (stock >= 0 && stock <= 3) lowCount++;
    }
  }

  // dédoublonner tailles
  const uniqueSizes = Array.from(new Set(sizes));

  return {
    count: vars.length,
    uniqueSizes,
    totalManaged,
    lowCount,
    hasAny,
  };
}

export default function StockOverview() {
  const [products, setProducts] = useState([]);
  const [qText, setQText] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const snap = await getDocs(query(collection(db, "products"), orderBy("title", "asc")));
        setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = norm(qText);
    if (!s) return products;

    return products.filter((p) => {
      const title = norm(p.title);
      const reference = norm(p.reference);
      const code = norm(p.code);

      const vars = Array.isArray(p.variants) ? p.variants : [];
      const skuStr = vars.map((v) => norm(v?.sku)).join(" ");
      const sizeStr = vars.map((v) => norm(v?.size)).join(" ");

      // si tu as déjà "search" array dans product (comme tes exemples)
      const searchArr = Array.isArray(p.search) ? p.search.map((x) => norm(x)).join(" ") : "";

      return (
        title.includes(s) ||
        reference.includes(s) ||
        code.includes(s) ||
        skuStr.includes(s) ||
        sizeStr.includes(s) ||
        searchArr.includes(s)
      );
    });
  }, [products, qText]);

  // KPIs rapides
  const kpis = useMemo(() => {
    let lowProducts = 0;
    let managedProducts = 0;

    for (const p of products) {
      const pStockManaged = typeof p.stock === "number" && p.stock >= 0;
      const vSum = summarizeVariants(p.variants);

      // Stock bas produit (si stock géré)
      const lowProductStock = pStockManaged && p.stock <= 3;

      // Stock bas variantes (si variantes gérées)
      const lowVariantStock = vSum.lowCount > 0;

      if (lowProductStock || lowVariantStock) lowProducts++;
      if (pStockManaged || vSum.count > 0) managedProducts++;
    }

    return { lowProducts, managedProducts, totalProducts: products.length };
  }, [products]);

  return (
    <div className="container py-4">
      <style>{`
        .soft-card { border: 0; box-shadow: 0 10px 22px rgba(0,0,0,.06); border-radius: 14px; }
        .img40 { width: 40px; height: 40px; object-fit: cover; border-radius: 10px; }
        .sizes-wrap { max-width: 280px; }
      `}</style>

      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h3 className="m-0">Stock (aperçu)</h3>
          <div className="text-muted small">
            Total: <b>{kpis.totalProducts}</b> • Produits avec alerte stock bas: <b>{kpis.lowProducts}</b>
          </div>
        </div>

        <input
          className="form-control"
          style={{ maxWidth: 380 }}
          placeholder="Rechercher (titre / ref / code / SKU / taille)"
          value={qText}
          onChange={(e) => setQText(e.target.value)}
        />
      </div>

      <div className="card soft-card">
        <div className="table-responsive">
          <table className="table table-hover align-middle m-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 70 }}>Image</th>
                <th>Produit</th>
                <th style={{ width: 160 }}>Réf / Code</th>
                <th style={{ width: 140 }}>Stock produit</th>
                <th>Variantes (tailles)</th>
                <th style={{ width: 210 }} className="text-end"></th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-muted">
                    Chargement…
                  </td>
                </tr>
              ) : (
                <>
                  {filtered.map((p) => {
                    const vSum = summarizeVariants(p.variants);

                    const pManaged = typeof p.stock === "number" && p.stock >= 0;
                    const pLow = pManaged && p.stock <= 3;

                    const vLow = vSum.lowCount > 0;
                    const anyLow = pLow || vLow;

                    return (
                      <tr key={p.id}>
                        <td>
                          {p.images?.[0] ? (
                            <img src={p.images[0]} alt="" className="img40" />
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>

                        <td>
                          <div className="fw-semibold">{p.title}</div>
                          <div className="text-muted small">
                            {p.reference ? p.reference : "—"} {p.code ? <> • <b>{p.code}</b></> : null}
                          </div>
                        </td>

                        <td className="text-muted small">
                          <div>Réf: {p.reference || "—"}</div>
                          <div>Code: {p.code || "—"}</div>
                        </td>

                        <td>
                          <span className={`badge ${anyLow ? "text-bg-warning" : "text-bg-secondary"}`}>
                            {displayStock(p.stock)}
                          </span>
                          {pLow && <span className="text-muted small ms-2">stock bas</span>}
                        </td>

                        <td>
                          {vSum.count ? (
                            <div>
                              <div className="d-flex flex-wrap gap-2 align-items-center">
                                <span className={`badge ${vLow ? "text-bg-warning" : "text-bg-light border text-dark"}`}>
                                  {vSum.count} variante(s)
                                </span>

                                <span className="badge text-bg-light border text-dark">
                                  Stock tailles: {vSum.totalManaged}
                                </span>

                                {vLow ? (
                                  <span className="badge text-bg-warning">
                                    {vSum.lowCount} taille(s) stock bas
                                  </span>
                                ) : null}
                              </div>

                              <div className="sizes-wrap d-flex flex-wrap gap-1 mt-2">
                                {vSum.uniqueSizes.slice(0, 12).map((s) => (
                                  <span key={s} className="badge text-bg-light border text-dark">
                                    {s}
                                  </span>
                                ))}
                                {vSum.uniqueSizes.length > 12 ? (
                                  <span className="badge text-bg-light border text-dark">
                                    +{vSum.uniqueSizes.length - 12}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted">Aucune</span>
                          )}
                        </td>

                        <td className="text-end">
                          <div className="btn-group">
                            <Link className="btn btn-outline-primary btn-sm" to={`/admin/stock/${p.id}`}>
                              Voir fiche
                            </Link>
                            <Link className="btn btn-outline-secondary btn-sm" to={`/admin/receptions/new`}>
                              Réception
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-4 text-muted">
                        Aucun produit trouvé.
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-muted small mt-2">
        Astuce: recherche aussi par <b>SKU</b> et par <b>taille</b> (ex: 42).
      </div>
    </div>
  );
}
